import { useEffect, useRef, useState } from "react";
import MainHome from "../../components/MainHome";
import "./../../styles/homePage/patientsList.scss";
import TablePage from "../../components/genericTable/TablePage";
import { useTranslation } from "react-i18next";
import { TableCard } from "../../common/types/tableCardType";
import { ReactComponent as ActiveSitesIcon } from "../../common/assats/homeScreens/cardsIcons/valid.svg";
import { ReactComponent as NonActiveSitesIcon } from "../../common/assats/homeScreens/cardsIcons/cross.svg";
import { useLocation, useNavigate } from "react-router-dom";
import {
  UsersDataResponseInterface,
  UserTableData,
} from "../../common/interfaces/usersData.interface";
import {
  deleteUsers,
  exportData,
  getUsersData,
  usersToggleAction,
} from "../../common/api/apiCalls";
import Filter from "../../components/common/Filter/Filter";
import { sysConfig } from "../../config";
import { useToast } from "../../common/contexts/useToast";
import { Positions } from "../../components/common/general/positions";
import { userInfoFields } from "../../components/usersList/UserInfoFields";
import { useFilters } from "../../common/contexts/FiltersContext";
import { AxiosResponse } from "axios";
import { useDebouncedSearch } from "../../common/hooks/Search";
import { getRolesByRoleName } from "../../common/function/generalFunctions";
import { FilterOptionInterface } from "../../common/interfaces/filtersObject.interface";
import { RouteEnum, roleToDisplayText } from "../../common/consts/roles";
import useUserStore from "../../common/store/user.store";

type SortOptions =
  | "fullName"
  | "username"
  | "phoneNumber"
  | "status"
  | "relatedSite"
  | "position"
  | "role";

const sortKeysArray: SortOptions[] = [
  "fullName",
  "username",
  "phoneNumber",
  "status",
  "relatedSite",
  "position",
  "role",
];

const UsersList = () => {
  const sortResetRef = useRef(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { search, debouncedSearch, handleSearchChange } = useDebouncedSearch(
    sysConfig.DEBOUNCE_TIME
  );
  const [sortKey, setSortKey] = useState<string>("username");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [usersCounts, setUsersCounts] = useState(0);
  const { activeFilters, clearFilters, handleCardFilters } = useFilters();
  const location = useLocation();
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const { showToast } = useToast();

  const [page, setPage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [usersData, setUsersData] = useState<UserTableData[]>([]);

  const [cards, setCards] = useState<TableCard[]>([]);

  const { canAccessCRUD } = useUserStore();
  const user = useUserStore((state) => state.user);

  const [filtersObject, setFiltersObject] = useState<FilterOptionInterface>({
    userStatus: {
      label: t("usersList.filters.userStatus"),
      options: [
        { name: t("sitePage.filters.active"), label: "Active" },
        { name: t("sitePage.filters.inactive"), label: "Inactive" },
      ],
    },
    relatedSite: {
      label: t("usersList.filters.relatedSite"),
      options: [],
    },
    positions: {
      label: t("usersList.filters.position"),
      options: Positions.map((position) => {
        return { name: t(position), label: t(position) };
      }).slice(0, -1),
    },
    role: {
      label: t("usersList.filters.role"),
      options: [] as { name: string; label: string }[],
    },
  });

  useEffect(() => {
    setPage(1);
    setUsersData([]);
    setHasMore(true);
  }, [sortKey, sortOrder]);

  
  useEffect(() => {
    fetchData();
  }, [sortKey, sortOrder, activeFilters, debouncedSearch, page]);

  const handleSortChange = (key: string, order: "asc" | "desc") => {
    const isKeyChanged = sortKey !== key;
    const isOrderChanged = sortOrder !== order;

    if (!isKeyChanged && !isOrderChanged) return;

    sortResetRef.current = true;
    setUsersData([]);
    setHasMore(true);
    setSortKey(key);
    setSortOrder(order);
    setPage(1);
  };

  const fetchData = async () => {
    try {
      const response = await getUsersData({
        sortKey,
        sortOrder,
        filters: activeFilters,
        search: debouncedSearch,
        page,
        limit: 10,
      });

      if (response?.data?.users?.length) {
        const filteredData = filterData(response.data.users);
        if (page === 1) {
          setUsersData(filteredData);
        } else {
          setUsersData((prevData) => [...prevData, ...filteredData]);
        }
        setHasMore(response.data.users.length > 0);

        setFiltersData(response.data?.sites);
      } else {
        setHasMore(false);
      }

      const currentUserId = user?.id;
      const currentUsername = user?.username?.toLowerCase();
      const currentUserOnThisPage = (response?.data?.users ?? []).some(
        (u: UsersDataResponseInterface) =>
          (currentUserId && String(u.id) === String(currentUserId)) ||
          (currentUsername &&
            (u.username ?? "").toLowerCase() === currentUsername)
      );

      const searchTerm = (debouncedSearch ?? "").trim().toLowerCase();
      const matchesSearch =
        !searchTerm ||
        (user?.username ?? "").toLowerCase().includes(searchTerm) ||
        (user?.fullName ?? "").toLowerCase().includes(searchTerm);

      const filterMatches = (filterName: string, value?: string) => {
        const selected = activeFilters?.[filterName];
        if (!selected?.length) return true;
        if (!value) return false;
        return selected.includes(value);
      };

      const currentUserStatusFromPage = (response?.data?.users ?? []).find(
        (u: UsersDataResponseInterface) =>
          (currentUserId && String(u.id) === String(currentUserId)) ||
          (currentUsername &&
            (u.username ?? "").toLowerCase() === currentUsername)
      )?.accountStatus;

      const currentUserStatus =
        (currentUserStatusFromPage ?? "Active").toLowerCase() === "inactive"
          ? "Inactive"
          : "Active";

      const matchesFilters =
        filterMatches("relatedSite", user?.site) &&
        filterMatches("positions", user?.position) &&
        filterMatches("role", user?.role) &&
        filterMatches("userStatus", currentUserStatus);

      const shouldExcludeCurrentUserFromCounts =
        !!(currentUserId || currentUsername) &&
        matchesSearch &&
        matchesFilters;

      const shouldAdjustCounts =
        currentUserOnThisPage || shouldExcludeCurrentUserFromCounts;

      const baseTotalCount = Number(response?.data?.count ?? 0);
      const adjustedTotalCount = Math.max(
        0,
        baseTotalCount - (shouldAdjustCounts ? 1 : 0)
      );
      setUsersCounts(adjustedTotalCount);

      let activeCount = Number(response?.data?.activeUsersCount ?? 0);
      let nonActiveCount = Number(response?.data?.nonActiveUsersCount ?? 0);

      if (shouldAdjustCounts) {
        if (currentUserStatus === "Inactive") {
          nonActiveCount = Math.max(0, nonActiveCount - 1);
        } else {
          activeCount = Math.max(0, activeCount - 1);
        }
      }

      setCardsData(activeCount, nonActiveCount);
    } catch (err) {
      showToast(
        "error",
        t("general.toast.error"),
        t("general.errors.generalError")
      );
    } finally {
      setIsFetchingMore(false);
    }
    sortResetRef.current = false;
  };


  const setCardsData = (
    activeUsersCount: number,
    nonActiveUsersCount: number
  ) => {
    setCards([
      {
        headline: t("usersList.cards.ActiveUsers"),
        explanation: t("patientsList.cards.afterExamExplain"),
        icon: ActiveSitesIcon,
        value: activeUsersCount,
        action: () => {
          handleCardFilters("userStatus", "Active");
          resetPagination();
        },
      },
      {
        headline: t("usersList.cards.nonActiveUsers"),
        explanation: t("patientsList.cards.afterExamExplain"),
        icon: NonActiveSitesIcon,
        value: nonActiveUsersCount,
        action: () => {
          handleCardFilters("userStatus", "Inactive");
          resetPagination();
        },
      },
    ]);
  };

  const setFiltersData = (sites: string[]) => {
    const arrRoles = getRolesByRoleName("");
    setFiltersObject((prevFilters) => ({
      ...prevFilters,
      relatedSite: {
        ...prevFilters.relatedSite,
        options: sites.map((site: string) => ({
          name: site,
          label: site,
        })),
      },
      role: {
        ...prevFilters.role,
        options: arrRoles.map((role: string) => ({
          name: role,
          label: role,
        })),
      },
    }));
  };

  const resetPagination = () => {
    setPage(1);
    setUsersData([]);
    setHasMore(true);
    setSelectedRows([]);
    setIsFetchingMore(false);
  };

  const navigateToUserDetails = (row: any) => {
    const userData = usersData.find(
      (user: UserTableData) => user.username === row.username
    );
    if (!userData) {
      showToast(
        "error",
        t("general.toast.error"),
        t("general.errors.generalError")
      );
      return;
    }
    const displayData = {
      position: userData?.position || "",
      role: userData.tableProps?.roles[0].name,
      email: userData.username,
      phoneNumber: userData?.phoneNumber || "",
      relatedSite: userData.tableProps?.sites?.[0]?.siteName || "",
    };
    navigate(`/Home/Users/View/${userData.tableProps.id}`, {
      state: {
        displayData: userInfoFields(displayData),
        editState: userData,
        informationData: {
          title: userData.fullName,
          status: userData.status,
          pathToEdit: `/Home/Users/Edit/${userData.tableProps.id}`,
          editPermission: RouteEnum.UsersEdit,
          userToEditRole: userData.tableProps?.roles[0].name
        },
      },
    });
  };

  const onEditClick = (row: any) => {
    const userData = usersData.find(
      (user: UserTableData) => user.username === row.username
    );

    if (!userData) {
      showToast(
        "error",
        t("general.toast.error"),
        t("general.errors.generalError")
      );
      return;
    }

    navigate(`/Home/Users/Edit/${userData.tableProps.id}`, {
      state: {
        ...userData,
        id: userData.tableProps.id,
      },
    });
  }

  const onToggleActionClick = async (row: any) => {
    const id = row?.tableProps?.id;
    const user = usersData.find((user) => user.tableProps?.id === id);
    if (!user) return;
    const res = await usersToggleAction([id]);
    if (res.success) {
      fetchData();
      resetPagination();
      showToast(
        "success",
        t("general.toast.success"),
        user?.tableProps?.activeStatus
          ? t("general.toast.deactivatedMessage").replace("{selected}", "user")
          : t("general.toast.activatedMessage").replace("{selected}", "user")
      )
    } else {
      showToast("error", t("general.toast.error"), t("general.errors.generalError"));
    }
  }




  const deleteSelectedUsers = async () => {
    const ids = selectedRows.map((row) => row.tableProps.id);
    const res = await deleteUsers(ids);
    if (res.success) {
      fetchData();
      resetPagination();
    } else {
      showToast("error", t("general.toast.error"), t("general.errors.generalError"));
    }
  }

  const navigateToAddUser = () => {
    navigate("/Home/Users/Create");
  };

  const searchChangeHandler = (str: string) => {
    handleSearchChange(str);
    resetPagination();
  };

  const handleCheckBoxClick = (selectedRows: any) => {
    const rowId = selectedRows.tableProps.id;

    const newSelectedRows = usersData.map((row: any) => {
      if (row.tableProps.id === rowId) {
        return {
          ...row,
          tableProps: {
            ...row.tableProps,
            selected: selectedRows.tableProps.selected,
          },
        };
      }
      return row;
    });

    setUsersData(newSelectedRows);
    if (selectedRows.tableProps.selected) {
      setSelectedRows((prev) => [...prev, selectedRows]);
    } else {
      setSelectedRows((prev) =>
        prev.filter((row) => row.tableProps.id !== rowId)
      );
    }
  };


  const exportUsers = async (type: string) => {
    try {
      const params = {
        type: "users",
        formats: type.toLowerCase(),
        sortKey,
        sortOrder,
        filters: activeFilters,
        search: debouncedSearch,
      };

      const response = await exportData("export", params);

      if (response.success && response.data) {
        const axiosResponse = response.data as AxiosResponse<any, any>;

        const contentDisposition = axiosResponse.headers["content-disposition"];
        const contentType = axiosResponse.headers["content-type"];
        const fileName = contentDisposition
          ? contentDisposition.split("filename=")[1]?.replace(/['"]/g, "")
          : contentType.includes("json")
            ? "export.json"
            : contentType.includes("spreadsheet")
              ? "export.xlsx"
              : "export.zip";

        const blob = new Blob([axiosResponse.data], { type: contentType });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        showToast(
          "error",
          t("general.toast.error"),
          t("general.export.exportGeneralError")
        );
      }
    } catch (error) {
      showToast(
        "error",
        t("general.toast.error"),
        t("general.export.exportGeneralError")
      );
    }
  };

  const handleFetchMore = () => {
    if (!isFetchingMore && hasMore && !sortResetRef.current) {
      setIsFetchingMore(true);
      setPage((prev) => prev + 1);
    }
  };


  const handleCheckAll = (e: any) => {
    const newSelectedRows = usersData.map((row: any) => {
      return {
        ...row,
        tableProps: {
          ...row.tableProps,
          selected: e,
        },
      };
    });
    setUsersData(newSelectedRows);
    setSelectedRows(e ? usersData : []);
  };

  const filterData = (data: any) => {
    const currentUserId = user?.id;
    const currentUsername = user?.username?.toLowerCase();

    return (data ?? [])
      .filter((u: UsersDataResponseInterface) => {
        if (!currentUserId && !currentUsername) return true;

        const idMatches =
          currentUserId && String(u.id) === String(currentUserId);
        const usernameMatches =
          currentUsername && (u.username ?? "").toLowerCase() === currentUsername;

        return !(idMatches || usernameMatches);
      })
      .map((u: UsersDataResponseInterface) => ({
        fullName: u.fullName,
        username: u.username,
        phoneNumber: u?.userInfo?.phoneNumber,
        status: u?.accountStatus || "Active",
        relatedSite: u.sites?.[0]?.siteName || "",
        position: u?.userInfo?.position || "",
        role: roleToDisplayText(u.roles[0]?.name),
        tableProps: {
          id: u.id,
          selected: false,
          sites: u.sites,
          roles: u.roles,
          firstName: u?.userInfo?.firstName,
          lastName: u?.userInfo?.lastName,
          middleName: u?.userInfo?.middleName,
          phoneNumber: u?.userInfo?.phoneNumber,
          honorific: u?.userInfo?.honorific,
          activeStatus: u?.accountStatus?.toLocaleLowerCase() === "active",
          systemMode: {
            isSystemModeDemo:
              !!u?.systemMode?.isSystemModeDemo || !!(u as any)?.isSystemModeDemo,
            isSystemModeProduct:
              !!u?.systemMode?.isSystemModeProduct || !!(u as any)?.isSystemModeProduct,
            isSystemModeClinical:
              !!u?.systemMode?.isSystemModeClinical || !!(u as any)?.isSystemModeClinical,
            isSystemModeResearch:
              !!u?.systemMode?.isSystemModeResearch || !!(u as any)?.isSystemModeResearch,
          },
        },
      }));
  };




  return (
    <MainHome>
      <Filter
        isOpen={isFilterOpen}
        filtersObject={filtersObject}
        handleFilterClose={() => {
          setIsFilterOpen(false);
        }}
        filterTrigger={resetPagination}
      />
      <TablePage
        hideCheck={true}
        tableHeadline={t(`usersList.usersList`)}
        headerHeadline={t("usersList.userStatus")}
        cards={cards}
        hasMore={hasMore}
        isFetchingMore={isFetchingMore}
        fetchMore={handleFetchMore}
        createPath="UsersCreate"
        totalCount={usersCounts}
        viewAllAction={() => {
          clearFilters();
          resetPagination();
        }}
        filterAction={() => {
          setIsFilterOpen(!isFilterOpen);
        }}
        searchValue={search}
        setSearchValue={searchChangeHandler}
        searchPlaceholder={t("table.filter.searchByEmailOrUsername")}
        buttonText={t("usersList.addUser")}
        buttonAction={navigateToAddUser}
        sortKey={sortKey}
        setSortKey={(key) => handleSortChange(key, sortOrder)}
        setSortOrder={(order) => handleSortChange(sortKey, order)}
        sortOrder={sortOrder}
        tableData={usersData}
        handleCheckAll={handleCheckAll}
        onCheckBoxClick={handleCheckBoxClick}
        sortKeysArray={
          sortKeysArray
            ? sortKeysArray.map((value) => {
              return { name: t(`usersList.sort.${value}`), val: value };
            })
            : []
        }
        onRowClick={(row: UsersDataResponseInterface) => {
          navigateToUserDetails(row);
        }}
        exportAction={exportUsers}
        isExport={true}
        canEdit={canAccessCRUD(RouteEnum.UsersEdit, user?.role)}
        canToggleActive={canAccessCRUD(RouteEnum.UserToggleAction, user?.role)}
        onEditClick={(row: any) => { onEditClick(row) }}
        onToggleActionClick={(row: any) => { onToggleActionClick(row) }}
      />
    </MainHome>
  );
};

export default UsersList;
